/**
 * Fiko3DPrintCost - 3D Baski Maliyet Hesaplama Modulu
 *
 * FDM, SLA, SLS, DMLS ve MJF surecleri icin
 * detayli maliyet hesaplama.
 *
 * Hesaplama: Malzeme hacmi + Destek malzemesi + Makine suresi + Son islem
 */

import { FikoMaterialDB, MaterialProperties, ManufacturingProcessType } from './FikoMaterialDB';
import { CostLineItem } from './FikoCostEngine';

export type Print3DProcessType = 'fdm_3d_print' | 'sla_3d_print' | 'sls_3d_print' | 'dmls_3d_print' | 'mjf_3d_print';

export interface Print3DCostInput {
    partGeometry: {
        volumeCm3: number;
        surfaceAreaCm2: number;
        boundingBox: { x: number; y: number; z: number };
        complexityScore: number;
    };
    material: MaterialProperties;
    batchSize: number;
    printProcess: Print3DProcessType;
    infillPercent?: number;         // FDM icin (varsayilan %20)
    layerHeightMM?: number;         // Katman yuksekligi
    supportRequired?: boolean;      // Destek gerekli mi (varsayilan: auto-detect)
    orientation?: 'auto' | 'x' | 'y' | 'z';
    postCuring?: boolean;           // SLA icin UV kur
}

export interface Print3DCostResult {
    costPerPart: number;
    costPerKg: number;
    breakdown: CostLineItem[];
    printTimeHours: number;
    materialUsedGrams: number;
    supportMaterialGrams: number;
    notes: string[];
    notesTR: string[];
}

export class Fiko3DPrintCost {
    private materialDB: FikoMaterialDB;

    constructor(materialDB: FikoMaterialDB) {
        this.materialDB = materialDB;
    }

    calculate(input: Print3DCostInput): Print3DCostResult {
        const { material, partGeometry, batchSize, printProcess } = input;
        const notes: string[] = [];
        const notesTR: string[] = [];
        const breakdown: CostLineItem[] = [];

        const partWeightKg = partGeometry.volumeCm3 * material.density;
        const partWeightGrams = partWeightKg * 1000;

        // 1. Efektif malzeme hacmi (infill dahil)
        const infillPercent = input.infillPercent ?? this.getDefaultInfill(printProcess);
        const effectiveVolumeCm3 = this.calculateEffectiveVolume(partGeometry.volumeCm3, printProcess, infillPercent);
        const materialGrams = effectiveVolumeCm3 * material.density * 1000;

        // 2. Malzeme maliyeti
        const materialPricePerKg = material.powderPricePerKg || material.pricePerKg;
        const materialCost = (materialGrams / 1000) * materialPricePerKg;

        breakdown.push({
            category: 'material',
            description: `${material.name} (${materialGrams.toFixed(1)}g, ${infillPercent}% infill, $${materialPricePerKg.toFixed(2)}/kg)`,
            descriptionTR: `${material.nameTR} (${materialGrams.toFixed(1)}g, %${infillPercent} doluluk, $${materialPricePerKg.toFixed(2)}/kg)`,
            quantity: materialGrams / 1000,
            unitCost: materialPricePerKg,
            totalCost: materialCost,
            unit: 'kg'
        });

        // 3. Destek malzemesi
        const needsSupport = input.supportRequired ?? this.estimateSupportsNeeded(partGeometry, printProcess);
        let supportGrams = 0;
        if (needsSupport) {
            const supportPercent = this.estimateSupportPercent(partGeometry, printProcess);
            supportGrams = materialGrams * (supportPercent / 100);
            const supportPricePerKg = materialPricePerKg * (printProcess === 'fdm_3d_print' ? 0.8 : 1.0);
            const supportCost = (supportGrams / 1000) * supportPricePerKg;

            breakdown.push({
                category: 'material',
                description: `Support material (${supportGrams.toFixed(1)}g, ${supportPercent.toFixed(0)}% of part)`,
                descriptionTR: `Destek malzemesi (${supportGrams.toFixed(1)}g, parcanin %${supportPercent.toFixed(0)}'i)`,
                quantity: supportGrams / 1000,
                unitCost: supportPricePerKg,
                totalCost: supportCost,
                unit: 'kg'
            });
        }

        // 4. Baski suresi
        const layerHeight = input.layerHeightMM ?? this.getDefaultLayerHeight(printProcess);
        const buildHeight = partGeometry.boundingBox.z; // mm
        const layerCount = Math.ceil(buildHeight / layerHeight);
        const printTimeHours = this.estimatePrintTime(partGeometry, printProcess, layerCount, effectiveVolumeCm3);

        // 5. Makine maliyeti
        const machineParams = this.materialDB.getMachineParams(printProcess as ManufacturingProcessType);
        const hourlyRate = machineParams?.hourlyRate || 30;
        const machineCost = printTimeHours * hourlyRate;

        // Batch icin: bazi surecler (SLS, MJF) build volume paylasilabilir
        const partsPerBuild = this.estimatePartsPerBuild(partGeometry, printProcess, batchSize);
        const machineCostPerPart = machineCost / partsPerBuild;

        breakdown.push({
            category: 'machine',
            description: `Print time ${printTimeHours.toFixed(1)}hr (${layerCount} layers x ${layerHeight}mm, ${partsPerBuild} parts/build, $${hourlyRate}/hr)`,
            descriptionTR: `Baski suresi ${printTimeHours.toFixed(1)}saat (${layerCount} katman x ${layerHeight}mm, ${partsPerBuild} parca/baski, $${hourlyRate}/saat)`,
            quantity: printTimeHours / partsPerBuild,
            unitCost: hourlyRate,
            totalCost: machineCostPerPart,
            unit: 'saat'
        });

        // 6. Son islemler
        // 6a. Destek kaldirma
        if (needsSupport) {
            const supportRemovalMinutes = this.estimateSupportRemovalTime(supportGrams, printProcess);
            const supportRemovalCost = (supportRemovalMinutes / 60) * 20; // $20/hr labor
            breakdown.push({
                category: 'post_processing',
                description: `Support removal (${supportRemovalMinutes.toFixed(0)} min)`,
                descriptionTR: `Destek kaldirma (${supportRemovalMinutes.toFixed(0)} dk)`,
                quantity: supportRemovalMinutes / 60,
                unitCost: 20,
                totalCost: supportRemovalCost,
                unit: 'saat'
            });
        }

        // 6b. UV kurleme (SLA)
        if (printProcess === 'sla_3d_print' && input.postCuring !== false) {
            breakdown.push({
                category: 'post_processing',
                description: 'UV post-curing (30 min)',
                descriptionTR: 'UV son kurleme (30 dk)',
                quantity: 0.5,
                unitCost: 10,
                totalCost: 5,
                unit: 'saat'
            });
        }

        // 6c. Yuzey temizleme / cilaklama
        const cleaningCost = this.getCleaningCost(printProcess, partGeometry.surfaceAreaCm2);
        breakdown.push({
            category: 'post_processing',
            description: 'Cleaning / surface finishing',
            descriptionTR: 'Temizlik / yuzey isleme',
            quantity: 1,
            unitCost: cleaningCost,
            totalCost: cleaningCost,
            unit: 'adet'
        });

        // 7. Hazirlik (build prep, slicing)
        const setupCost = this.getSetupCost(printProcess) / Math.min(batchSize, partsPerBuild);
        breakdown.push({
            category: 'setup',
            description: `Build preparation (slicing, orientation) / ${Math.min(batchSize, partsPerBuild)} parts`,
            descriptionTR: `Baski hazirligi (dilimleme, yonlendirme) / ${Math.min(batchSize, partsPerBuild)} parca`,
            quantity: 1,
            unitCost: setupCost,
            totalCost: setupCost,
            unit: 'adet'
        });

        // 8. Iscilik
        const laborMinutes = 5 + (needsSupport ? 10 : 0); // Load/unload + support removal
        const laborCost = (laborMinutes / 60) * 20;
        breakdown.push({
            category: 'labor',
            description: `Operator (${laborMinutes} min)`,
            descriptionTR: `Operator (${laborMinutes} dk)`,
            quantity: laborMinutes / 60,
            unitCost: 20,
            totalCost: laborCost,
            unit: 'saat'
        });

        // Toplam
        const costPerPart = breakdown.reduce((sum, item) => sum + item.totalCost, 0);
        const costPerKg = partWeightKg > 0 ? costPerPart / partWeightKg : 0;

        notes.push(`Print time: ${printTimeHours.toFixed(1)} hours (${layerCount} layers)`);
        notes.push(`Material: ${materialGrams.toFixed(0)}g part + ${supportGrams.toFixed(0)}g support`);
        notes.push(`Parts per build: ${partsPerBuild}`);
        notesTR.push(`Baski suresi: ${printTimeHours.toFixed(1)} saat (${layerCount} katman)`);
        notesTR.push(`Malzeme: ${materialGrams.toFixed(0)}g parca + ${supportGrams.toFixed(0)}g destek`);
        notesTR.push(`Baski basina parca: ${partsPerBuild}`);

        if (batchSize > 100 && (printProcess === 'fdm_3d_print' || printProcess === 'sla_3d_print')) {
            notes.push('TIP: For 100+ parts, consider SLS/MJF or injection molding');
            notesTR.push('IPUCU: 100+ parca icin SLS/MJF veya enjeksiyon kaliplama dusunun');
        }

        return {
            costPerPart: Math.round(costPerPart * 100) / 100,
            costPerKg: Math.round(costPerKg * 100) / 100,
            breakdown,
            printTimeHours,
            materialUsedGrams: materialGrams,
            supportMaterialGrams: supportGrams,
            notes,
            notesTR,
        };
    }

    // ============================================================
    // HELPERS
    // ============================================================

    private getDefaultInfill(process: Print3DProcessType): number {
        if (process === 'fdm_3d_print') return 20;
        return 100; // SLA, SLS, DMLS, MJF are solid
    }

    private calculateEffectiveVolume(volumeCm3: number, process: Print3DProcessType, infillPercent: number): number {
        if (process === 'fdm_3d_print') {
            // Shell (2 walls) + infill
            const shellPercent = 15; // ~15% is shell/walls
            return volumeCm3 * (shellPercent / 100 + (1 - shellPercent / 100) * (infillPercent / 100));
        }
        return volumeCm3; // Solid for other processes
    }

    private estimateSupportsNeeded(geom: any, process: Print3DProcessType): boolean {
        // SLS and MJF are self-supporting (powder bed)
        if (process === 'sls_3d_print' || process === 'mjf_3d_print') return false;
        // Others likely need support for overhangs
        return geom.complexityScore > 30;
    }

    private estimateSupportPercent(geom: any, process: Print3DProcessType): number {
        const basePercent = 15 + geom.complexityScore * 0.3;
        if (process === 'dmls_3d_print') return basePercent * 1.5; // Metal needs more support
        return basePercent;
    }

    private getDefaultLayerHeight(process: Print3DProcessType): number {
        const heights: Record<Print3DProcessType, number> = {
            fdm_3d_print: 0.2,
            sla_3d_print: 0.05,
            sls_3d_print: 0.1,
            dmls_3d_print: 0.04,
            mjf_3d_print: 0.08,
        };
        return heights[process] || 0.1;
    }

    private estimatePrintTime(geom: any, process: Print3DProcessType, layerCount: number, volumeCm3: number): number {
        // Time per layer (seconds) varies by process
        const baseTimePerLayer: Record<Print3DProcessType, number> = {
            fdm_3d_print: 60,      // Slow, line-by-line
            sla_3d_print: 15,      // Flash per layer
            sls_3d_print: 30,      // Laser scan per layer
            dmls_3d_print: 45,     // Metal laser, slower
            mjf_3d_print: 12,      // Fast, full-layer
        };

        // Volume factor for FDM (more volume = more extrusion time)
        let volumeFactor = 1.0;
        if (process === 'fdm_3d_print') {
            volumeFactor = 1 + volumeCm3 * 0.05;
        }

        const totalSeconds = layerCount * (baseTimePerLayer[process] || 30) * volumeFactor;
        return totalSeconds / 3600; // hours
    }

    private estimatePartsPerBuild(geom: any, process: Print3DProcessType, batchSize: number): number {
        // Build volumes (mm)
        const buildVolumes: Record<Print3DProcessType, { x: number; y: number; z: number }> = {
            fdm_3d_print: { x: 250, y: 250, z: 300 },
            sla_3d_print: { x: 145, y: 145, z: 175 },
            sls_3d_print: { x: 340, y: 340, z: 600 },
            dmls_3d_print: { x: 250, y: 250, z: 325 },
            mjf_3d_print: { x: 380, y: 284, z: 380 },
        };
        const bv = buildVolumes[process] || { x: 250, y: 250, z: 300 };
        const spacing = 5; // mm between parts

        const partsX = Math.floor(bv.x / (geom.boundingBox.x + spacing));
        const partsY = Math.floor(bv.y / (geom.boundingBox.y + spacing));
        const partsZ = (process === 'sls_3d_print' || process === 'mjf_3d_print')
            ? Math.floor(bv.z / (geom.boundingBox.z + spacing))
            : 1; // Only powder bed processes can stack vertically

        const maxPerBuild = Math.max(1, partsX * partsY * partsZ);
        return Math.min(batchSize, maxPerBuild);
    }

    private estimateSupportRemovalTime(supportGrams: number, process: Print3DProcessType): number {
        if (process === 'sla_3d_print') return 5 + supportGrams * 0.1; // Snap off + sand
        if (process === 'dmls_3d_print') return 15 + supportGrams * 0.3; // Wire EDM / grinding
        return 5 + supportGrams * 0.15; // FDM: break off + sand
    }

    private getCleaningCost(process: Print3DProcessType, surfaceAreaCm2: number): number {
        const rates: Record<Print3DProcessType, number> = {
            fdm_3d_print: 0.5,
            sla_3d_print: 1.5,    // IPA wash + UV
            sls_3d_print: 1.0,    // Powder removal + bead blast
            dmls_3d_print: 3.0,   // Stress relief + surface finish
            mjf_3d_print: 0.8,    // Bead blast
        };
        return (rates[process] || 1.0) + surfaceAreaCm2 * 0.002;
    }

    private getSetupCost(process: Print3DProcessType): number {
        const costs: Record<Print3DProcessType, number> = {
            fdm_3d_print: 5,
            sla_3d_print: 10,
            sls_3d_print: 30,
            dmls_3d_print: 50,
            mjf_3d_print: 25,
        };
        return costs[process] || 10;
    }
}
